import type React from "react"
import { useState, useEffect } from "react"
import { useAppDispatch } from "@/app/hooks"
import { createReview, updateReview } from "@/features/siteReviews/siteReviewsSlice"
import { Modal } from "@/components/ui"
import { Star } from "lucide-react"
import type { ReviewResponse } from "@/types/review"
import "./ReviewFormModal.css"

type ReviewFormModalProps = {
  isOpen: boolean
  onClose: () => void
  siteId?: string
  review?: ReviewResponse | null
}

export const ReviewFormModal: React.FC<ReviewFormModalProps> = ({
  isOpen,
  onClose,
  siteId,
  review,
}) => {
  const dispatch = useAppDispatch()
  
  const [rating, setRating] = useState<number>(5)
  const [hoverRating, setHoverRating] = useState<number | null>(null)
  const [comment, setComment] = useState<string>("")
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize form when editing review or opening/closing
  useEffect(() => {
    if (isOpen) {
      if (review) {
        setRating(review.rating)
        setComment(review.comment)
      } else {
        setRating(5)
        setComment("")
      }
      setError(null)
      setSubmitting(false)
    }
  }, [isOpen, review])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!comment.trim()) {
      setError("리뷰 내용을 입력해주세요.")
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      if (review) {
        // Edit mode
        await dispatch(
          updateReview({
            reviewId: review.id,
            request: { rating, comment },
          }),
        ).unwrap()
      } else if (siteId) {
        // Create mode
        await dispatch(
          createReview({
            campingSiteId: siteId,
            rating,
            comment,
          }),
        ).unwrap()
      }
      onClose()
    } catch (err) {
      console.error("Failed to submit review:", err)
      setError("리뷰 등록에 실패했습니다. 다시 시도해주세요.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    void handleSubmit(e)
  }

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    if (text.length <= 1000) {
      setComment(text)
      if (error) setError(null)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={review ? "리뷰 수정하기" : "리뷰 작성하기"}
      size="md"
    >
      <form className="review-form" onSubmit={handleFormSubmit}>
        <div className="rating-select-container">
          <label className="form-label">방문은 어떠셨나요? 별점을 선택해 주세요.</label>
          <div className="star-rating-buttons">
            {Array.from({ length: 5 }).map((_, i) => {
              const starValue = i + 1
              const isActive = hoverRating !== null ? starValue <= hoverRating : starValue <= rating

              return (
                <button
                  type="button"
                  key={i.toString()}
                  className={`star-button ${isActive ? "active" : ""}`}
                  onClick={() => { setRating(starValue); }}
                  onMouseEnter={() => { setHoverRating(starValue); }}
                  onMouseLeave={() => { setHoverRating(null); }}
                  aria-label={`${starValue.toString()}점 주기`}
                >
                  <Star
                    size={36}
                    fill={isActive ? "currentColor" : "none"}
                    strokeWidth={isActive ? 0 : 1.5}
                  />
                </button>
              )
            })}
          </div>
          <span className="rating-text-hint">
            {hoverRating !== null 
              ? `${hoverRating.toString()}점 (${hoverRating === 5 ? "최고예요" : hoverRating === 4 ? "좋아요" : hoverRating === 3 ? "보통이에요" : hoverRating === 2 ? "별로예요" : "최악이에요"})`
              : `${rating.toString()}점 (${rating === 5 ? "최고예요" : rating === 4 ? "좋아요" : rating === 3 ? "보통이에요" : rating === 2 ? "별로예요" : "최악이에요"})`
            }
          </span>
        </div>

        <div className="comment-input-container">
          <label htmlFor="review-comment" className="form-label">리뷰 내용</label>
          <textarea
            id="review-comment"
            className="review-textarea"
            placeholder="이 캠핑장에 대한 솔직한 후기를 남겨주세요. (주변 경치, 시설 편의성, 관리 상태 등)"
            value={comment}
            onChange={handleCommentChange}
            disabled={submitting}
            required
          />
          <div className="textarea-footer">
            {error && <span className="form-error-message">{error}</span>}
            <span className="char-counter">{comment.length} / 1000자</span>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            취소
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={submitting || !comment.trim()}
          >
            {submitting ? "제출 중..." : review ? "수정완료" : "등록하기"}
          </button>
        </div>
      </form>
    </Modal>
  )
}
